import { useCallback, useContext, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

import { Button } from 'core-components/library/button'
import { NotificationController } from 'core-components/library/popup'
import {
  Controller,
  isValidEmail,
  ValidateInput,
  ValidationStatus,
} from 'core-components/library/validation'
import { selectAppId } from 'core-components/sitebuilder/store/selectors/selectAppId'
import { appEventBus, AppEvents } from 'src/core/classes/eventBuses/AppEventBus'
import {
  FIELDS_CONFIG,
  SUCCESS_NOTIFICATION_TEXT,
} from 'src/core/components/app/constants/registration'
import { AppContext } from 'src/core/components/app/context/AppContext'
import { useLogoutMutation } from 'src/core/store/service/sitebuilder/userApi'

import styles from './RegistrationForm.module.scss'

export interface IUserQuery {
  Email: string
  FirstName: string
  LastName: string
  SelectRole: string
  SelectCountry: string
  SelectMarket: string
}

export const Registration = () => {
  const [user, setUser] = useState<IUserQuery>({
    Email: '',
    FirstName: '',
    LastName: '',
    SelectRole: '',
    SelectCountry: '',
    SelectMarket: '',
  })
  const [validStatus, setValidStatus] = useState(ValidationStatus.Invalid)
  const [isSubmitClicked, setIsSubmitClicked] = useState<boolean>(false)
  const [isLogoutClicked, setIsLogoutClicked] = useState<boolean>(false)
  const [logout] = useLogoutMutation()
  const { userSettings } = useContext(AppContext)

  const appId = useSelector(selectAppId)

  const onChange = useCallback((value: string, field: string) => {
    setUser((prev) => ({ ...prev, [field]: value }))
  }, [])

  const validFn = useCallback(
    async (value: string, label: string, required: boolean): Promise<string> => {
      if (!required) {
        return ''
      }
      if (!value) {
        return `Field '${label}' must not be empty`
      }
      if (label === 'Email') {
        return userSettings?.allowedEmailsValidator
          ? userSettings.allowedEmailsValidator(value)
          : isValidEmail(value)
      }
      return ''
    },
    [userSettings]
  )

  const renderTextField = useCallback(
    (label: string, field: string, required: boolean) => {
      return (
        <div className={styles.field} key={field}>
          <ValidateInput
            label={label}
            onChange={(value) => onChange(value, field)}
            value={user ? user[field] : ''}
            checkFn={(value: string) => validFn(value, label, required)}
          />
        </div>
      )
    },
    [onChange, user, validFn]
  )

  const onSubmit = useCallback(() => {
    setIsSubmitClicked(true)
    appEventBus.notify(AppEvents.onRoleRequest, {
      appId,
      userInfo: {
        FirstName: user.FirstName,
        LastName: user.LastName,
        QueryRole: user.SelectRole,
        Creator: user.Email,
        Email: user.Email,
        Country: user.SelectCountry,
        Market: user.SelectMarket,
      },
      simulation: false,
      isRegistration: true,
    })
  }, [appId, user])

  useEffect(() => {
    return appEventBus.subscribe(AppEvents.onRoleRequestAnswer, (_, { isSuccess, message }) => {
      if (isSuccess) {
        NotificationController.showSuccess({ message: SUCCESS_NOTIFICATION_TEXT })
      } else {
        setIsSubmitClicked(false)
        NotificationController.showError({ message })
      }
    })
  }, [])

  return (
    <div className={styles.root}>
      <div className={styles.card}>
        <div className={styles.inner}>
          <div>
            <span className={styles.title}>
              You don't have permission to access to current app. You can ask it:
            </span>
            <Controller
              onValidationStatusChange={({ status }) => {
                status !== ValidationStatus.WIP && status !== validStatus && setValidStatus(status)
              }}
            >
              {FIELDS_CONFIG.map((field) => {
                return renderTextField(field.label, field.field, field.required)
              })}
            </Controller>
            <div className={styles.footer}>
              <Button
                disabled={validStatus === ValidationStatus.Invalid || isSubmitClicked}
                variant={'contained'}
                className={styles.btn}
                color={'primary'}
                onClick={onSubmit}
              >
                Submit
              </Button>

              <Button
                color={'danger'}
                variant={'outlined'}
                loading={isLogoutClicked}
                onClick={async () => {
                  setIsLogoutClicked(true)
                  await logout({ logoutFromHp: true })
                }}
              >
                Logout
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
